import { useState } from 'react';
import { Search, Filter, ChevronDown, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import type { EmployeeSelectionFilters } from '@/services/payroll/types';

interface EmployeeFiltersProps {
  filters: EmployeeSelectionFilters;
  onFiltersChange: (filters: EmployeeSelectionFilters) => void;
  availableRoles: string[];
}

export const EmployeeFilters = ({
  filters,
  onFiltersChange,
  availableRoles,
}: EmployeeFiltersProps) => {
  const [isOpen, setIsOpen] = useState(true);

  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, searchQuery: value });
  };

  const handleRoleToggle = (role: string) => {
    const newRoles = filters.roles.includes(role)
      ? filters.roles.filter(r => r !== role)
      : [...filters.roles, role];
    onFiltersChange({ ...filters, roles: newRoles });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      searchQuery: '',
      roles: [],
      hasApprovedEntries: true,
    });
  };

  const activeFilterCount = 
    (filters.searchQuery ? 1 : 0) + 
    filters.roles.length;

  return (
    <div className="space-y-4">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-center justify-between">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="flex items-center gap-2 w-full justify-start p-0 hover:bg-transparent">
              <Filter className="h-4 w-4" />
              <span className="font-semibold">Filters</span>
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {activeFilterCount}
                </Badge>
              )}
              <ChevronDown className={`h-4 w-4 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </Button>
          </CollapsibleTrigger>
          {activeFilterCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearAllFilters}
              className="h-8 px-2 text-muted-foreground hover:text-foreground"
            >
              Clear
            </Button>
          )}
        </div>

        <CollapsibleContent className="space-y-4 pt-4">
          {/* Active Filters Summary */}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap gap-2 pb-2">
              {filters.searchQuery && (
                <Badge variant="outline" className="gap-1">
                  Search: {filters.searchQuery}
                  <X 
                    className="h-3 w-3 cursor-pointer hover:text-destructive" 
                    onClick={() => handleSearchChange('')}
                  />
                </Badge>
              )}
              {filters.roles.map(role => (
                <Badge key={role} variant="outline" className="gap-1">
                  {role}
                  <X 
                    className="h-3 w-3 cursor-pointer hover:text-destructive" 
                    onClick={() => handleRoleToggle(role)}
                  />
                </Badge>
              ))}
            </div>
          )}

          {/* Search Input */}
          <div className="space-y-2">
            <Label htmlFor="search" className="text-xs font-medium text-muted-foreground uppercase">
              Search Employees
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="search"
                placeholder="Search by name or email..."
                value={filters.searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            {filters.searchQuery && (
              <p className="text-xs text-muted-foreground">
                Searching for: <span className="font-medium">{filters.searchQuery}</span>
              </p>
            )}
          </div>

          {/* Role Filters */}
          {availableRoles.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground uppercase">
                Filter by Role
              </Label>
              <div className="space-y-2">
                {availableRoles.map((role) => (
                  <div 
                    key={role} 
                    className="flex items-center space-x-2 hover:bg-accent/50 p-2 rounded-md transition-colors cursor-pointer"
                    onClick={() => handleRoleToggle(role)}
                  >
                    <Checkbox
                      id={`role-${role}`}
                      checked={filters.roles.includes(role)}
                      onCheckedChange={() => handleRoleToggle(role)}
                    />
                    <label
                      htmlFor={`role-${role}`}
                      className="text-sm font-medium leading-none cursor-pointer flex-1"
                    >
                      {role}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
